import { CalendarIcon } from '@/components/icons/calendarIcon';
import { useConfigStore } from '@/stores';
import { useOrientation } from '@/hooks';
import type { ListTypeListItem } from '@/types/list-type';
import { formatKoreanDateTTS, formatListTypeDateRange } from '@/utils/date-time';
import clsx from 'clsx';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import PlaceHolder from '@/components/icons/shop/PlaceHolder';
import { getS3UploadUrl, isS3UploadUrl } from '@/utils/s3Config';

interface CardTypeCarouselItemProps {
    content: ListTypeListItem;
    isActive: boolean;
}

const CardTypeCarouselItem = memo(({ content, isActive }: CardTypeCarouselItemProps) => {
    const { t } = useTranslation();
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const { isHorizontal } = useOrientation();
    const isHalfMode = screenMode === 'split';
    const title = content.title || '';
    const body = content.contents || '';
    const dateRange = formatListTypeDateRange(content.eventDateBegin, content.eventDateEnd, t);
    const dateRangeTTS = formatKoreanDateTTS(content.eventDateBegin, content.eventDateEnd, t);

    const url = content.contentsUrl.url;
    const urlPreview: string | null | undefined = url ? (isS3UploadUrl(url) ? url : getS3UploadUrl(url)) : '';

    const tabImg = isActive ? 2 : -1;
    const tabTitle = isActive ? 3 : -1;
    const tabDate = isActive ? 4 : -1;
    const tabContent = isActive ? 5 : -1;

    return (
        <div
            className={clsx(styles.card, {
                [styles['card--horizontal']]: isHorizontal,
                [styles['card--vertical']]: !isHorizontal,
                [styles['card--halfmode']]: !isHorizontal && isHalfMode,
            })}
        >
            <div
                tabIndex={tabImg}
                data-interactive
                data-region-nav={isActive}
                data-region-order={isActive ? 2 : undefined}
                aria-label={t('list_type.detail.tts.detail_image', { title })}
                className={clsx('focus-border', styles.card__image, {
                    [styles['card__image--horizontal']]: isHorizontal,
                    [styles['card__image--vertical']]: !isHorizontal,
                    [styles['card__image--halfmode']]: isHalfMode,
                })}
            >
                {urlPreview ? <img src={urlPreview} alt={title} loading="lazy" /> : <PlaceHolder />}
            </div>

            {/* Text body */}
            <div
                className={clsx(styles.card__body, {
                    [styles['card__body--horizontal']]: isHorizontal,
                    [styles['card__body--vertical']]: !isHorizontal,
                    [styles['card__body--halfmode']]: isHalfMode,
                    [styles['card__body--contrast']]: contrast,
                })}
            >
                <div
                    data-region-nav={isActive}
                    data-region-order={isActive ? 3 : undefined}
                >
                    <div tabIndex={tabTitle} data-interactive aria-label={title} className={clsx('focus-border', styles.card__title)}>
                        {title.length > 16 ? `${title.slice(0, 16)}...` : title}
                    </div>

                    <div tabIndex={tabDate} data-interactive aria-label={dateRangeTTS} className={clsx('focus-border', styles.card__date)}>
                        <CalendarIcon width={18} height={18} color={!contrast ? '#444444' : '#ffffff'} />
                        <span>{dateRange}</span>
                    </div>
                </div>

                <div
                    tabIndex={tabContent}
                    data-interactive
                    aria-label={body}
                    className={clsx('focus-border', styles.card__content, {
                        [styles['card__content--horizontal']]: isHorizontal,
                    })}
                    data-region-nav={isActive}
                    data-region-order={isActive ? 4 : undefined}
                >
                    {body}
                </div>
            </div>
        </div>
    );
});

CardTypeCarouselItem.displayName = 'CardTypeCarouselItem';

export default CardTypeCarouselItem;
